const MOBILE_AUTH_MODE = "mobile";
const DEFAULT_TOKEN_REQUEST_TIMEOUT_MS = 30000;
const TOKEN_EXPIRATION_BUFFER_SECONDS = 30;

const MESSAGE_TYPE = {
  tokenRequest: "AUTH_TOKEN_REQUEST",
  tokenExpired: "AUTH_TOKEN_EXPIRED",
  tokenResponse: "AUTH_TOKEN_RESPONSE",
  tokenUpdated: "AUTH_TOKEN_UPDATED",
  authError: "AUTH_ERROR",
} as const;

type TMobileBridge = {
  postMessage: (message: string) => void;
};

declare global {
  interface Window {
    ReactNativeWebView?: TMobileBridge;
    __MY_MONTEPIO_AUTH_MODE__?: string;
    __MY_MONTEPIO_MOBILE_WEBVIEW__?: boolean;
    __MY_MONTEPIO_FONT_SCALE__?: number;
  }
}

export type TMobileAuthMessage = {
  type?: string;
  idToken?: string;
  token?: string;
  accessToken?: string;
  payload?: unknown;
  message?: string;
  error?: string;
  [key: string]: unknown;
};

type TMobileAuthMessageWithType = TMobileAuthMessage & {
  type: string;
};

type TJwtClaims = {
  exp?: number;
  [key: string]: unknown;
};

type TTokenRequestOptions = {
  forceRefresh?: boolean;
  timeout?: number;
};

type TPendingTokenRequest = {
  forceRefresh: boolean;
  promise: Promise<string>;
};

type TMobileAuthState = {
  idToken: string;
  pendingTokenRequest: TPendingTokenRequest | null;
};

const mobileAuthState: TMobileAuthState = {
  idToken: "",
  pendingTokenRequest: null,
};

export function isMobileAuthMode(): boolean {
  const requestedAuthMode = new URLSearchParams(window.location.search).get("authMode");

  return (
    window.__MY_MONTEPIO_AUTH_MODE__ === MOBILE_AUTH_MODE ||
    window.__MY_MONTEPIO_MOBILE_WEBVIEW__ === true ||
    requestedAuthMode === MOBILE_AUTH_MODE
  );
}

export function hasMobileBridge(): boolean {
  return typeof window.ReactNativeWebView?.postMessage === "function";
}

export function postMobileAuthMessage(type: string, payload: object = {}): boolean {
  if (!hasMobileBridge()) return false;

  const message = JSON.stringify({ type, ...payload });
  window.ReactNativeWebView?.postMessage(message);

  return true;
}

export function parseMobileAuthMessage(event: unknown): TMobileAuthMessage | null {
  if (!isRecord(event)) return null;

  const eventData = event.data;

  if (!eventData) return null;
  if (isRecord(eventData)) return eventData as TMobileAuthMessage;
  if (typeof eventData !== "string") return null;

  try {
    const parsedMessage: unknown = JSON.parse(eventData);

    return isRecord(parsedMessage) ? (parsedMessage as TMobileAuthMessage) : null;
  } catch {
    return { type: eventData };
  }
}

export function addMobileAuthMessageListener(
  callback: (message: TMobileAuthMessageWithType) => void,
): () => void {
  const handleMessage = (event: Event) => {
    const message = parseMobileAuthMessage(event);

    if (typeof message?.type === "string") {
      callback(message as TMobileAuthMessageWithType);
    }
  };

  window.addEventListener("message", handleMessage);
  document.addEventListener("message", handleMessage);

  return () => {
    window.removeEventListener("message", handleMessage);
    document.removeEventListener("message", handleMessage);
  };
}

export function getTokenFromMobileAuthMessage(message: unknown): string {
  if (!isRecord(message)) return "";

  const payload = isRecord(message.payload) ? message.payload : null;

  return firstString(message.idToken, payload?.idToken);
}

export function requestMobileAuthToken({
  forceRefresh = false,
  timeout = DEFAULT_TOKEN_REQUEST_TIMEOUT_MS,
}: TTokenRequestOptions = {}): Promise<string> {
  const pendingRequest = mobileAuthState.pendingTokenRequest;

  if (pendingRequest) {
    if (!forceRefresh || pendingRequest.forceRefresh) {
      return pendingRequest.promise;
    }

    const requestForcedToken = () => requestMobileAuthToken({ forceRefresh: true, timeout });

    return pendingRequest.promise.then(requestForcedToken, requestForcedToken);
  }

  if (!forceRefresh && canReuseCachedToken()) {
    return Promise.resolve(mobileAuthState.idToken);
  }

  return startMobileTokenRequest({ forceRefresh, timeout });
}

export function clearMobileAuthTokenCache(): void {
  mobileAuthState.idToken = "";
}

function startMobileTokenRequest({
  forceRefresh,
  timeout,
}: Required<TTokenRequestOptions>): Promise<string> {
  let resolveRequest!: (token: string) => void;
  let rejectRequest!: (reason?: unknown) => void;

  const request = new Promise<string>((resolve, reject) => {
    resolveRequest = resolve;
    rejectRequest = reject;
  });

  mobileAuthState.pendingTokenRequest = {
    forceRefresh,
    promise: request,
  };

  let hasSettled = false;
  let timeoutId: ReturnType<typeof setTimeout>;
  let removeMessageListener = () => {};

  const settleRequest = <T>(settle: (value: T) => void, value: T) => {
    if (hasSettled) return;

    hasSettled = true;
    clearTimeout(timeoutId);
    removeMessageListener();

    if (mobileAuthState.pendingTokenRequest?.promise === request) {
      mobileAuthState.pendingTokenRequest = null;
    }

    settle(value);
  };

  const rejectWithMessage = (message: string) => {
    settleRequest(rejectRequest, new Error(message));
  };

  removeMessageListener = addMobileAuthMessageListener((message) => {
    const token = getTokenFromMobileAuthMessage(message);

    const isTokenResponse =
      message.type === MESSAGE_TYPE.tokenResponse || message.type === MESSAGE_TYPE.tokenUpdated;

    if (isTokenResponse && token) {
      cacheToken(token);
      settleRequest(resolveRequest, token);
      return;
    }

    if (message.type === MESSAGE_TYPE.authError) {
      rejectWithMessage(getMobileAuthErrorMessage(message));
    }
  });

  timeoutId = setTimeout(() => {
    rejectWithMessage("Timeout ao aguardar token da app mobile");
  }, timeout);

  const requestType = forceRefresh ? MESSAGE_TYPE.tokenExpired : MESSAGE_TYPE.tokenRequest;

  if (!postMobileAuthMessage(requestType)) {
    rejectWithMessage("Bridge mobile indisponivel");
  }

  return request;
}

function canReuseCachedToken(): boolean {
  if (!mobileAuthState.idToken) return false;
  if (isJwtExpired(mobileAuthState.idToken, TOKEN_EXPIRATION_BUFFER_SECONDS)) return false;

  return true;
}

function cacheToken(idToken: string): void {
  mobileAuthState.idToken = idToken;
}

function parseJwt(token: string): TJwtClaims | null {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    const claims: unknown = JSON.parse(atob(padded));

    return isRecord(claims) ? (claims as TJwtClaims) : null;
  } catch {
    return null;
  }
}

function isJwtExpired(token: string, bufferSeconds = 0): boolean {
  const claims = parseJwt(token);

  if (!claims?.exp) return true;

  return claims.exp < Date.now() / 1000 + bufferSeconds;
}

function getMobileAuthErrorMessage(message: TMobileAuthMessage): string {
  if (typeof message.message === "string" && message.message) return message.message;
  if (typeof message.error === "string" && message.error) return message.error;

  return "Erro de autenticacao mobile";
}

function firstString(...values: unknown[]): string {
  return (
    values.find((value): value is string => typeof value === "string" && value.length > 0) || ""
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
